import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, Facebook, Instagram, Sparkles, Check, Copy, Loader2, ImagePlus } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * Iter-338 — customer post step: tap-out links to the store's real
 * pages + suggested captions (photo-aware). Endpoint NEVER gates the
 * post step — template fallback is still usable, failures just hide
 * the cards. Copy rule: NEVER the word "AI" in customer-facing text.
 *
 * Iter-462 — social-post parity: IG/TikTok can't prefill captions via
 * deep link, so we copy the chosen caption to the clipboard *before*
 * opening the app. Copy must be synchronous (execCommand) because the
 * new tab steals focus and async Clipboard API writes then reject.
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

/**
 * Iter-462 — synchronous copy for use right before a deep-link out.
 * execCommand runs inside the same user gesture and completes before
 * the new tab takes focus; async writeText is a fire-and-forget
 * fallback for browsers that removed execCommand.
 */
export function copyTextBeforeNav(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) return true;
  } catch {
    // fall through to async fallback
  }
  try {
    navigator.clipboard?.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Iter-645 → Iter-689 — the customer's OWN composer per platform, CREATE
 * screen first. Ordered native candidates (web uses the first; the 1.4 s
 * visibility fallback opens the web composer when the app isn't there).
 */
const IS_IOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

function composerAppUrl(platform) {
  if (platform === "facebook") return "fb://composer";
  // iOS: library picker → camera; Android: share flow → camera.
  if (platform === "instagram") return IS_IOS() ? "instagram://library" : "instagram://share";
  if (platform === "tiktok") return "snssdk1233://studio/create";
  return null;
}

export function composerWebUrl(platform, caption, shareUrl) {
  if (platform === "facebook")
    return (
      "https://www.facebook.com/sharer/sharer.php?u=" +
      // Iter 779 — share the backend's store card (`vendor.share_url`,
      // store-specific OG title + 1200×630 logo card) so the Facebook
      // post is about the STORE, not a cropped site wordmark. Fallback
      // to the site root only when the vendor payload has no share_url.
      encodeURIComponent(shareUrl || "https://allsale.co.nz") +
      (caption ? "&quote=" + encodeURIComponent(caption) : "")
    );
  // Iter 689 — the CREATE screen, not the home feed.
  if (platform === "instagram") return "https://www.instagram.com/create/select/";
  if (platform === "tiktok") return "https://www.tiktok.com/upload";
  return null;
}

/**
 * Iter-645 — deep-link the installed app first on mobile; if the page is
 * still visible after ~1.4 s (app not installed) open the web URL.
 */
function deepLinkThenWeb(appUrl, webUrl) {
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (appUrl && isMobile) {
    const t = setTimeout(() => {
      if (!document.hidden) window.open(webUrl, "_blank", "noopener");
    }, 1400);
    const vis = () => {
      if (document.hidden) clearTimeout(t);
      document.removeEventListener("visibilitychange", vis);
    };
    document.addEventListener("visibilitychange", vis);
    try {
      window.location.href = appUrl;
    } catch {
      // timer opens the web URL
    }
  } else {
    window.open(webUrl, "_blank", "noopener");
  }
}

export function openOwnComposer(platform, caption, shareUrl) {
  const webUrl = composerWebUrl(platform, caption, shareUrl);
  if (!webUrl) return;
  deepLinkThenWeb(composerAppUrl(platform), webUrl);
}

/**
 * Iter-689 §2 — "Open {Platform} to copy the link": the page where the
 * customer's fresh post lives, so Share → Copy link is one tap away.
 */
const COPY_LINK_APP = {
  facebook: "fb://profile",
  instagram: "instagram://app",
  tiktok: "snssdk1233://user/profile/self",
};
const COPY_LINK_WEB = {
  facebook: "https://www.facebook.com/me",
  instagram: "https://www.instagram.com/",
  tiktok: "https://www.tiktok.com/",
  google: "https://www.google.com/maps/contrib/",
};

export function openCopyLinkPage(platform) {
  const webUrl = COPY_LINK_WEB[platform] || COPY_LINK_WEB.facebook;
  deepLinkThenWeb(COPY_LINK_APP[platform] || null, webUrl);
}

/**
 * Iter-645 — append the vendor-set tag for this platform + @allsale
 * #AllSale, skipping anything the caption already contains (the caption
 * ideas often include the store tag already).
 */
export function withPlatformTags(text, platform, tags, allsaleTag = "@allsale") {
  const parts = [tags?.[platform], allsaleTag, "#AllSale"].filter(Boolean);
  const lower = (text || "").toLowerCase();
  const missing = parts.filter((p) => !lower.includes(String(p).toLowerCase()));
  const base = (text || "").trim();
  if (missing.length === 0) return base;
  return base ? `${base} ${missing.join(" ")}` : missing.join(" ");
}

export default function SocialPostHelper({ sessionId, links, platform, vendorId, onTapOut, photoB64, shareUrl }) {
  const [suggestions, setSuggestions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const fileRef = useRef(null);

  // Iter-442+ §3 — per-platform availability for the brand-icon picker.
  const platformsQ = useQuery({
    queryKey: ["vendor-platforms", vendorId],
    enabled: !!vendorId,
    retry: 0,
    staleTime: 60_000,
    queryFn: async () =>
      (await sharedApi.get(`/classifieds/engagement/vendors/${vendorId}/platforms`, OPTS)).data,
  });
  const brandPlatforms = platformsQ.data?.platforms || null;

  const hasLinks = !!(links && (links.instagram || links.facebook || links.tiktok || links.google_review_url));
  if (!hasLinks && !sessionId && !brandPlatforms) return null;

  const fetchSuggestions = async (photoB64) => {
    setBusy(true);
    try {
      const body = { platform: platform || "instagram" };
      if (photoB64) body.photo_b64 = photoB64;
      const { data } = await sharedApi.post(
        `/classifieds/engagement/sessions/${sessionId}/post-suggestions`,
        body,
        OPTS,
      );
      setSuggestions(data);
      setCopiedIdx(null);
    } catch {
      toast.error("Couldn't fetch caption ideas — you can still post your own");
    } finally {
      setBusy(false);
    }
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || "").split(",")[1] || "";
      if (b64) fetchSuggestions(b64);
    };
    reader.readAsDataURL(file);
  };

  const copyCaption = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older in-app browsers (Instagram/Facebook webviews) block the
      // Clipboard API — fall back to the legacy textarea trick.
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        toast.error("Couldn't copy — long-press the text instead");
        return;
      }
    }
    setCopiedIdx(i);
    toast.success("Copied!");
  };

  const tagHandle = suggestions?.vendor_tag || links?.instagram_handle;

  // Iter-462 — the caption we carry out on deep-link: the one the user
  // tapped, else the first suggestion (if any were generated).
  const pendingCaption =
    (copiedIdx != null && suggestions?.suggestions?.[copiedIdx]) ||
    suggestions?.suggestions?.[0] ||
    null;

  // Iter-462 — copy caption BEFORE opening IG/TikTok (they can't
  // prefill captions from a URL). Google/Facebook don't need it.
  const copyBeforeNav = (platformName) => {
    if (!pendingCaption) return;
    if (platformName !== "instagram" && platformName !== "tiktok") return;
    if (copyTextBeforeNav(pendingCaption)) {
      toast.success("Caption copied — paste it into your post");
    }
  };

  const onBrandTap = (p) => {
    copyBeforeNav(p.platform);
    window.open(p.url, "_blank", "noopener");
    onTapOut?.(p.platform);
    if (sessionId && !suggestions) fetchSuggestions();
  };

  // Iter-521 → Iter-645 — post on the CUSTOMER'S OWN profile: caption =
  // {tapped suggestion} + vendor-set tag for THAT platform + @allsale
  // #AllSale (no duplicates), copied to the clipboard, then the
  // customer's own composer opens (app deep link → web fallback).
  const postOnOwnProfile = (p) => {
    const tags = platformsQ.data?.tags || links?.tags || {};
    const allsaleTag = platformsQ.data?.allsale_tag || links?.allsale_tag || "@allsale";
    const caption = withPlatformTags(pendingCaption || "", p.platform, tags, allsaleTag);
    if (caption && copyTextBeforeNav(caption)) {
      toast.success("✅ Caption copied — paste it into your post, then come back and tap Submit.");
    }
    // Iter 779 — Facebook card = the store's share page, not the site root.
    openOwnComposer(p.platform, caption, shareUrl || platformsQ.data?.share_url);
    onTapOut?.(p.platform);
    if (sessionId && !suggestions) fetchSuggestions();
  };

  return (
    <div className="mt-3 space-y-3" data-testid="engage-social-helper">
      {/* Iter-521 — per-platform rows: ① Follow their page ② Post on
          YOUR profile. Google keeps its single review button. */}
      {brandPlatforms && (
        <div className="space-y-2" data-testid="engage-platform-icons">
          {brandPlatforms.map((p) => {
            const own = p.platform === "facebook" || p.platform === "instagram" || p.platform === "tiktok";
            return (
              <div key={p.platform} className="flex flex-wrap items-center gap-2" data-testid={`engage-platform-row-${p.platform}`}>
                <BrandIcon p={p} onTap={() => onBrandTap(p)} />
                {p.platform === "google" && p.url && (
                  <button
                    type="button"
                    data-testid="engage-google-review-btn"
                    onClick={() => onBrandTap(p)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-2 transition-colors"
                  >
                    ⭐ Review on Google
                  </button>
                )}
                {own && (
                  <>
                    {p.url && (
                      <button
                        type="button"
                        data-testid={`engage-follow-${p.platform}`}
                        onClick={() => onBrandTap(p)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-2 transition-colors"
                      >
                        Follow their page
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`engage-own-${p.platform}`}
                      onClick={() => postOnOwnProfile(p)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3.5 py-2 transition-colors"
                    >
                      Post on YOUR profile
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {hasLinks && !brandPlatforms && (
        <>
          <div className="flex flex-wrap gap-2">
            {links.google_review_url && (
              <TapOut href={links.google_review_url} testid="engage-social-google" icon={Star} label="Review on Google" onBeforeNav={() => onTapOut?.("google")} />
            )}
            {links.facebook && (
              <TapOut href={links.facebook} testid="engage-social-facebook" icon={Facebook} label="Their Facebook" onBeforeNav={() => onTapOut?.("facebook")} />
            )}
            {links.instagram && (
              <TapOut
                href={links.instagram}
                testid="engage-social-instagram"
                icon={Instagram}
                label="Their Instagram"
                onBeforeNav={() => { copyBeforeNav("instagram"); onTapOut?.("instagram"); }}
              />
            )}
            {/* Iter-412 §2 — TikTok tag-out (spec copy, literal). */}
            {links.tiktok && (
              <a
                href={links.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { copyBeforeNav("tiktok"); onTapOut?.("tiktok"); }}
                data-testid="engage-social-tiktok"
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-2 transition-colors"
              >
                🎵 Their TikTok
              </a>
            )}
          </div>
        </>
      )}
      {(hasLinks || brandPlatforms) && (
        <>
          <p className="text-xs text-slate-500" data-testid="engage-social-tagline">
            Tag {tagHandle || "the store"} and {platformsQ.data?.allsale_tag || links?.allsale_tag || "@allsale"} in your post.
          </p>
          {/* Iter-412 §6 — review composer, only when the store has a
              Google review link. Spec-approved customer copy. */}
          {links?.google_review_url && sessionId && <ReviewDraftComposer sessionId={sessionId} />}
        </>
      )}

      {sessionId && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="engage-suggest-photo"
            disabled={busy}
            onClick={() => (photoB64 ? fetchSuggestions(photoB64) : fileRef.current?.click())}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3.5 py-2 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
            Suggest caption from my photo
          </button>
          <button
            type="button"
            data-testid="engage-suggest-nophoto"
            disabled={busy}
            onClick={() => fetchSuggestions(null)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-2 transition-colors disabled:opacity-60"
          >
            <Sparkles className="w-3.5 h-3.5" /> No photo
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="engage-suggest-file" />
        </div>
      )}

      {suggestions?.suggestions?.length > 0 && (
        <ul className="space-y-2" data-testid="engage-suggestion-list">
          {suggestions.suggestions.map((cap, i) => (
            <li key={i}>
              <button
                type="button"
                data-testid={`engage-suggestion-${i}`}
                onClick={() => copyCaption(cap, i)}
                className="w-full text-left rounded-xl border border-slate-200 bg-white hover:border-slate-400 px-3.5 py-2.5 text-xs text-slate-700 leading-relaxed transition-colors"
              >
                <span className="flex items-start gap-2">
                  {copiedIdx === i
                    ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    : <Copy className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />}
                  <span className="flex-1">{cap}</span>
                </span>
                {copiedIdx === i && (
                  <span className="mt-1 block pl-5 font-bold text-emerald-600">
                    Copied — now tap Instagram or TikTok above and paste
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Iter-442+ §3 — brand icon button. Greyed out when the store has no
 * URL for the platform, or (Google only) when `available` is false
 * (review already done and not repeatable).
 */
function BrandIcon({ p, onTap }) {
  const enabled = !!p.url && !(p.platform === "google" && p.available === false);
  const label = { google: "Review on Google", facebook: "Their Facebook", instagram: "Their Instagram", tiktok: "Their TikTok" }[p.platform] || p.platform;
  const base = "w-11 h-11 rounded-full grid place-items-center shadow-sm border transition-transform";
  const looks = {
    google: `${base} bg-white border-slate-200`,
    facebook: `${base} bg-[#1877F2] border-transparent text-white`,
    instagram: `${base} bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] border-transparent text-white`,
    tiktok: `${base} bg-black border-transparent text-white`,
  };
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      data-testid={`engage-platform-${p.platform}`}
      disabled={!enabled}
      onClick={onTap}
      className={`${looks[p.platform] || `${base} bg-white border-slate-200`} ${
        enabled ? "hover:scale-105 cursor-pointer" : "opacity-35 grayscale cursor-not-allowed"
      }`}
    >
      {p.platform === "google" && (
        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z" />
          <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.8-3.8H1.2v3.1A12 12 0 0 0 12 24z" />
          <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.2a12 12 0 0 0 0 10.8l4.1-3.1z" />
          <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.2 6.6l4.1 3.1A7.2 7.2 0 0 1 12 4.8z" />
        </svg>
      )}
      {p.platform === "facebook" && <Facebook className="w-5 h-5" fill="currentColor" strokeWidth={0} />}
      {p.platform === "instagram" && <Instagram className="w-5 h-5" />}
      {p.platform === "tiktok" && (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
          <path d="M19.6 6.3a5 5 0 0 1-3.6-3.8V2h-3.3v13.2a2.8 2.8 0 1 1-2-2.7V9.1a6.1 6.1 0 1 0 5.3 6V8.9a8.2 8.2 0 0 0 4.4 1.3V6.9c-.3 0-.6-.2-.8-.6z" />
        </svg>
      )}
    </button>
  );
}

function TapOut({ href, icon: Icon, label, testid, onBeforeNav }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onBeforeNav}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-2 transition-colors"
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </a>
  );
}

/**
 * Iter-412 §6 — "✨ Write my review for me" (spec: the ✨ IS
 * customer-facing). POST /sessions/{id}/review-draft {notes?} →
 * {review} → tap-to-copy box. Never mentions the word "AI".
 */
function ReviewDraftComposer({ sessionId }) {
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const body = {};
      if (notes.trim()) body.notes = notes.trim();
      const { data } = await sharedApi.post(
        `/classifieds/engagement/sessions/${sessionId}/review-draft`,
        body,
        OPTS,
      );
      if (data?.review) {
        setDraft(data.review);
        setCopied(false);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't write a draft — you can still write your own");
    } finally {
      setBusy(false);
    }
  };

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = draft;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        toast.error("Couldn't copy — long-press the text instead");
        return;
      }
    }
    setCopied(true);
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="engage-review-composer">
      <div className="flex items-center gap-2">
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What did you love? (optional)"
          maxLength={200}
          data-testid="engage-review-notes-input"
          className="flex-1 min-w-0 rounded-full bg-white border border-slate-200 focus:border-slate-400 px-4 py-2 text-xs focus:outline-none"
        />
        <button
          type="button"
          data-testid="engage-review-draft-btn"
          disabled={busy}
          onClick={generate}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3.5 py-2 transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          ✨ Write my review for me
        </button>
      </div>
      {draft && (
        <button
          type="button"
          data-testid="engage-review-draft-copy"
          onClick={copyDraft}
          className="mt-2 w-full text-left rounded-xl border border-slate-200 bg-white hover:border-slate-400 px-3.5 py-2.5 text-xs text-slate-700 leading-relaxed transition-colors"
        >
          <span className="flex items-start gap-2">
            {copied
              ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
              : <Copy className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />}
            <span className="flex-1">{draft}</span>
          </span>
          {copied && (
            <span className="mt-1 block pl-5 font-bold text-emerald-600" data-testid="engage-review-draft-copied">
              ✅ Copied — now tap ⭐ Review on Google and paste
            </span>
          )}
        </button>
      )}
    </div>
  );
}
